/* Azha Perfumes — delivery location picker + PDP/cart delivery estimate.
   Uses the shared AzhaDrawer module (assets/app.js) for the picker panel's
   open/close/backdrop/Escape handling — this file only owns area selection,
   geolocation, and the business-day delivery-date math. */
(function () {
  'use strict';

  const STORAGE_KEY = 'azha_delivery_location';
  const SKIP_KEY = 'azha_delivery_skipped';
  const SKIP_TTL_MS = 24 * 60 * 60 * 1000; // re-prompt once a day after a skip/close
  const UAE_TZ = 'Asia/Dubai';

  let areasData = null;
  let festivalDates = null; // Set of "YYYY-MM-DD" strings to skip like Sundays

  function getModal() {
    return document.querySelector('[data-drawer="delivery-location"]');
  }

  function readJSON(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (_error) {
      return null;
    }
  }

  function getLocation() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  function setLocation(emirate, area) {
    const value = { emirate, area, setAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    localStorage.removeItem(SKIP_KEY);
    renderAll();
    document.dispatchEvent(new CustomEvent('azha:delivery-location-change', { detail: value }));
    if (window.AzhaDrawer) window.AzhaDrawer.close('delivery-location');
  }

  function clearLocation() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SKIP_KEY);
    renderAll();
    document.dispatchEvent(new CustomEvent('azha:delivery-location-change', { detail: null }));
  }

  function markSkipped() {
    localStorage.setItem(SKIP_KEY, String(Date.now()));
  }

  function isSkippedRecently() {
    const raw = localStorage.getItem(SKIP_KEY);
    if (!raw) return false;
    const skippedAt = parseInt(raw, 10);
    if (!Number.isFinite(skippedAt)) return false;
    return Date.now() - skippedAt < SKIP_TTL_MS;
  }

  function shouldAutoOpen() {
    return !getLocation() && !isSkippedRecently();
  }

  function buildAreaList(filter) {
    const modal = getModal();
    if (!modal || !areasData) return;

    const groups = modal.querySelectorAll('[data-delivery-emirate]');
    let total = 0;
    const q = (filter || '').trim().toLowerCase();

    groups.forEach((groupEl) => {
      const emirate = groupEl.getAttribute('data-delivery-emirate');
      const ul = groupEl.querySelector('[data-delivery-emirate-list]');
      const items = Object.keys(areasData[emirate] || {}).filter((area) => {
        if (!q) return true;
        return area.toLowerCase().includes(q) || emirate.toLowerCase().includes(q);
      });

      const nextChildren = items.map((area) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'w-full px-2 py-2 text-left text-sm text-primary hover:bg-sand';
        btn.textContent = area;
        btn.setAttribute('data-delivery-pick-emirate', emirate);
        btn.setAttribute('data-delivery-pick-area', area);
        li.appendChild(btn);
        return li;
      });

      ul.replaceChildren(...nextChildren);
      groupEl.hidden = items.length === 0;
      total += items.length;
    });

    const noResults = modal.querySelector('[data-delivery-no-results]');
    if (noResults) noResults.classList.toggle('hidden', total > 0);
  }

  function durationFor(emirate, area) {
    const group = areasData && areasData[emirate];
    if (!group) return null;
    const days = group[area];
    return Number.isFinite(days) ? days : null;
  }

  function renderAll() {
    const loc = getLocation();

    document.querySelectorAll('[data-delivery-location-target]').forEach((el) => {
      if (!loc) {
        el.setAttribute('data-state', 'empty');
        return;
      }

      el.querySelectorAll('[data-delivery-area]').forEach((areaEl) => {
        areaEl.textContent = `${loc.emirate} – ${loc.area}`;
      });

      const days = durationFor(loc.emirate, loc.area);
      if (days === 0) {
        el.setAttribute('data-state', 'unserviceable');
        return;
      }
      if (days == null) {
        // Stored area is no longer in the data set (list changed) — re-prompt.
        el.setAttribute('data-state', 'empty');
        return;
      }

      el.setAttribute('data-state', 'selected');
      const getItByEl = el.querySelector('[data-delivery-get-it-by]');
      if (getItByEl) getItByEl.textContent = formatGetItBy(loc.emirate, loc.area);
      const countdownEl = el.querySelector('[data-delivery-countdown]');
      if (countdownEl) {
        const cutoffHour = parseInt(el.getAttribute('data-delivery-cutoff-hour') || '12', 10);
        countdownEl.textContent = formatCountdown(cutoffHour);
      }
    });
  }

  function getLocaleTag() {
    const lang = getModal()?.getAttribute('data-locale') || document.documentElement.lang || 'en';
    return lang.startsWith('ar') ? 'ar-AE' : 'en-GB';
  }

  // ─── UAE-timezone business-day math (no date library; UAE has no DST) ────

  function getUAEParts(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: UAE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    const map = {};
    parts.forEach((part) => {
      map[part.type] = part.value;
    });

    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      hour: parseInt(map.hour, 10),
      minute: parseInt(map.minute, 10),
    };
  }

  function ymdString({ year, month, day }) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  function ymdValue({ year, month, day }) {
    return Date.UTC(year, month - 1, day);
  }

  function addDays({ year, month, day }, amount) {
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + amount);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
  }

  function weekdayOf({ year, month, day }) {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sunday
  }

  function isSkipDate(ymd) {
    if (weekdayOf(ymd) === 0) return true; // Sunday
    if (festivalDates && festivalDates.has(ymdString(ymd))) return true; // Closed days
    return false;
  }

  // Compute the expected arrival date for the given duration in business days.
  //   - Cutoff is cutoffHour (default 12 PM UAE). If "now" is past the cutoff
  //     the order is processed the next day, so add 1 day to duration.
  //   - Sunday and any merchant-configured festival/holiday closed dates do not
  //     count as a delivery day.
  function computeArrivalDate(durationDays, cutoffHour) {
    const now = getUAEParts(new Date());
    let remaining = durationDays;
    if (now.hour >= cutoffHour) remaining += 1;

    let cursor = { year: now.year, month: now.month, day: now.day };
    while (remaining > 0) {
      cursor = addDays(cursor, 1);
      if (!isSkipDate(cursor)) remaining -= 1;
    }
    return cursor;
  }

  // Returns "Saturday, 23 May" for the customer's area, computed in UAE time.
  function formatGetItBy(emirate, area, cutoffHour) {
    const days = durationFor(emirate, area);
    if (days == null || days <= 0) return ''; // unknown or non-serviceable area
    const cutoff = Number.isFinite(cutoffHour) ? cutoffHour : 12;
    const arrival = computeArrivalDate(days, cutoff);
    const displayDate = new Date(Date.UTC(arrival.year, arrival.month - 1, arrival.day, 12));
    return new Intl.DateTimeFormat(getLocaleTag(), {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(displayDate);
  }

  // "Order within Xh Ym" — time left until today's cutoff (UAE time).
  // After the cutoff, counts down to tomorrow's cutoff.
  function formatCountdown(cutoffHour) {
    const now = getUAEParts(new Date());
    const nowMinutes = now.hour * 60 + now.minute;
    let diff = cutoffHour * 60 - nowMinutes;
    if (diff <= 0) diff += 24 * 60;
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return `${hours}h ${minutes}m`;
  }

  function parseYMD(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const date = new Date(Date.UTC(year, month - 1, day));
    // Rejects rollover dates (e.g. 2026-02-30) — UTC round-trip won't match.
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
      return null;
    }
    return { year, month, day };
  }

  // Single festival window {start, end}. Both must be valid YYYY-MM-DD; start
  // must not be after end; range expansion is capped at 366 days to guard
  // against typos like 1900 - 2099.
  function loadFestivalDates() {
    festivalDates = new Set();
    const range = readJSON('delivery-festival-range') || {};
    const start = parseYMD((range.start || '').trim());
    const end = parseYMD((range.end || '').trim());
    if (!start || !end || ymdValue(end) < ymdValue(start)) return;

    let cursor = start;
    let safety = 0;
    while (ymdValue(cursor) <= ymdValue(end) && safety < 366) {
      festivalDates.add(ymdString(cursor));
      cursor = addDays(cursor, 1);
      safety += 1;
    }
  }

  // ─── Geolocation (Mapbox reverse-geocode + IP fallback) ──────────────────

  function showGeoError(modal, messageKey) {
    const el = modal.querySelector('[data-delivery-geo-error]');
    if (!el) return;
    el.textContent = modal.dataset[messageKey] || '';
    el.classList.remove('hidden');
  }

  function clearGeoError(modal) {
    modal.querySelector('[data-delivery-geo-error]')?.classList.add('hidden');
  }

  function setGeolocateLabel(modal, text) {
    const el = modal.querySelector('[data-delivery-geolocate-label]');
    if (el) el.textContent = text;
  }

  function interpolate(template, vars) {
    let text = template || '';
    Object.keys(vars || {}).forEach((key) => {
      text = text.replace(new RegExp(`\\{${key}\\}`, 'g'), vars[key]);
    });
    return text;
  }

  function matchAreaFromMapbox(features) {
    const candidates = [];
    features.forEach((feature) => {
      if (feature.place_name) candidates.push(feature.place_name.toLowerCase());
      if (feature.text) candidates.push(feature.text.toLowerCase());
    });

    for (const emirate of Object.keys(areasData)) {
      for (const area of Object.keys(areasData[emirate] || {})) {
        if (candidates.some((c) => c.includes(area.toLowerCase()))) {
          return { emirate, area };
        }
      }
      if (candidates.some((c) => c.includes(emirate.toLowerCase()))) {
        return { emirate, area: null };
      }
    }
    return null;
  }

  function getDetectedLocation(features) {
    if (!features || !features.length) return null;
    for (const feature of features) {
      const context = feature.context || [];
      const country = context.find((c) => c.id && c.id.startsWith('country'));
      const place = context.find((c) => c.id && (c.id.startsWith('place') || c.id.startsWith('region')));
      if (country) {
        return {
          country: country.text,
          countryCode: (country.short_code || '').toUpperCase(),
          place: place ? place.text : feature.text,
        };
      }
    }
    const first = features[0];
    return { country: null, countryCode: null, place: first.text || first.place_name || '' };
  }

  function handleGeolocate(modal) {
    const token = modal.dataset.mapboxToken;
    if (!token || !navigator.geolocation) {
      showGeoError(modal, 'geolocationError');
      return;
    }

    clearGeoError(modal);
    const labelEl = modal.querySelector('[data-delivery-geolocate-label]');
    const originalLabel = labelEl ? labelEl.textContent : '';
    setGeolocateLabel(modal, modal.dataset.detectingLabel || originalLabel);

    const onPositionFix = (position) => {
      const { latitude, longitude } = position.coords;
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${encodeURIComponent(token)}&types=neighborhood,locality,district,place,address,region,country`;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`mapbox http ${res.status}`);
          return res.json();
        })
        .then((json) => {
          setGeolocateLabel(modal, originalLabel);
          const features = json.features || [];
          const detected = getDetectedLocation(features);

          if (detected && detected.countryCode && detected.countryCode !== 'AE') {
            const where = detected.place ? `${detected.place}, ${detected.country}` : detected.country;
            modal.querySelector('[data-delivery-geo-error]').textContent = interpolate(modal.dataset.geolocationOutsideUae, { location: where });
            modal.querySelector('[data-delivery-geo-error]').classList.remove('hidden');
            return;
          }

          const match = matchAreaFromMapbox(features);
          if (match) {
            const area = match.area || (areasData[match.emirate] && Object.keys(areasData[match.emirate])[0]);
            if (area) {
              setLocation(match.emirate, area);
              return;
            }
          }

          if (detected && (detected.place || detected.country)) {
            const where = detected.place || detected.country;
            modal.querySelector('[data-delivery-geo-error]').textContent = interpolate(modal.dataset.geolocationInsideNoMatch, { location: where });
            modal.querySelector('[data-delivery-geo-error]').classList.remove('hidden');
          } else {
            showGeoError(modal, 'geolocationNoMatch');
          }
        })
        .catch(() => {
          setGeolocateLabel(modal, originalLabel);
          showGeoError(modal, 'geolocationError');
        });
    };

    // When the browser Geolocation API can't get coordinates from the OS
    // (Wi-Fi off, location services off, no GPS), fall back to server-side IP
    // geolocation so we can at least tell the customer which country we detected.
    const ipFallback = () => {
      fetch('https://ipapi.co/json/')
        .then((res) => {
          if (!res.ok) throw new Error(`ipapi http ${res.status}`);
          return res.json();
        })
        .then((ip) => {
          setGeolocateLabel(modal, originalLabel);
          const countryCode = (ip.country_code || '').toUpperCase();
          const country = ip.country_name || '';
          const place = ip.city || ip.region || country;

          if (countryCode && countryCode !== 'AE') {
            const where = place ? `${place}, ${country}` : country;
            modal.querySelector('[data-delivery-geo-error]').textContent = interpolate(modal.dataset.geolocationOutsideUae, { location: where });
            modal.querySelector('[data-delivery-geo-error]').classList.remove('hidden');
            return;
          }
          if (countryCode === 'AE') {
            modal.querySelector('[data-delivery-geo-error]').textContent = interpolate(modal.dataset.geolocationInsideNoMatch, { location: place || modal.dataset.regionUae || '' });
            modal.querySelector('[data-delivery-geo-error]').classList.remove('hidden');
            return;
          }
          showGeoError(modal, 'geolocationError');
        })
        .catch(() => {
          setGeolocateLabel(modal, originalLabel);
          showGeoError(modal, 'geolocationError');
        });
    };

    const onPositionError = (error) => {
      // POSITION_UNAVAILABLE: retry once with GPS hardware if available.
      if (error && error.code === 2) {
        navigator.geolocation.getCurrentPosition(onPositionFix, ipFallback, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
        return;
      }
      // PERMISSION_DENIED: skip IP lookup — would feel like a privacy workaround.
      if (error && error.code === 1) {
        setGeolocateLabel(modal, originalLabel);
        showGeoError(modal, 'geolocationError');
        return;
      }
      // TIMEOUT and anything else: try IP fallback.
      ipFallback();
    };

    navigator.geolocation.getCurrentPosition(onPositionFix, onPositionError, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 600000,
    });
  }

  // ─── Wiring ────────────────────────────────────────────────────────────

  function tickCountdowns() {
    if (!getLocation()) return;
    document.querySelectorAll('[data-delivery-location-target][data-state="selected"] [data-delivery-countdown]').forEach((el) => {
      const wrap = el.closest('[data-delivery-location-target]');
      const cutoffHour = parseInt(wrap?.getAttribute('data-delivery-cutoff-hour') || '12', 10);
      el.textContent = formatCountdown(cutoffHour);
    });
  }

  function bindModalListeners(modal) {
    modal.querySelector('[data-delivery-search]')?.addEventListener('input', (event) => {
      buildAreaList(event.target.value);
    });

    modal.addEventListener('click', (event) => {
      const pickBtn = event.target.closest('[data-delivery-pick-emirate]');
      if (pickBtn) {
        setLocation(pickBtn.getAttribute('data-delivery-pick-emirate'), pickBtn.getAttribute('data-delivery-pick-area'));
        return;
      }
      if (event.target.closest('[data-delivery-geolocate]')) {
        handleGeolocate(modal);
        return;
      }
      if (event.target.closest('[data-delivery-skip]')) {
        markSkipped();
        if (window.AzhaDrawer) window.AzhaDrawer.close('delivery-location');
      }
    });
  }

  // Dismissing the drawer without picking an area still counts as "handled" —
  // otherwise shouldAutoOpen() stays true and it reopens every refresh.
  // Track open state ourselves: Drawer.close() (X button / backdrop / Escape)
  // closes whichever drawer is open without passing an id, so drawer:close
  // can't be matched by id the way drawer:open can.
  let deliveryModalOpen = false;
  document.addEventListener('drawer:open', (event) => {
    deliveryModalOpen = event.detail?.id === 'delivery-location';
  });
  document.addEventListener('drawer:close', () => {
    if (deliveryModalOpen && !getLocation()) markSkipped();
    deliveryModalOpen = false;
  });

  function init() {
    const modal = getModal();
    if (!modal) return;

    // Single source of truth: { emirate: { area: durationDays } }.
    areasData = readJSON('delivery-area-durations') || {};
    loadFestivalDates();
    bindModalListeners(modal);
    buildAreaList('');
    renderAll();
    if (shouldAutoOpen() && window.AzhaDrawer) window.AzhaDrawer.open('delivery-location');
    setInterval(tickCountdowns, 60000);
  }

  window.Azha = window.Azha || {};
  window.Azha.DeliveryLocation = {
    get: getLocation,
    set: setLocation,
    clear: clearLocation,
    open: () => window.AzhaDrawer && window.AzhaDrawer.open('delivery-location'),
    close: () => window.AzhaDrawer && window.AzhaDrawer.close('delivery-location'),
    render: renderAll,
    formatGetItBy,
    formatCountdown,
    durationFor,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
